import type { CollisionShape } from "./collision-authoring.ts";
import { cellFootprint } from "./coords.ts";
import { ellipseCells, lineCells, rectCells } from "./shapes.ts";

/**
 * The collision brush shapes (ADR-040) applied to a frame's own cell grid in the footprint editor, so a
 * footprint is authored with the same pencil, square, ellipse, rectangle and line as a collision layer.
 */

/**
 * A cell of the frame grid, columns from the left and rows up from the bottom.
 */
export interface FrameCell {
  readonly x: number;
  readonly y: number;
}

/**
 * The frame cells a brush covers: stamp shapes at the end cell, drag shapes from the start to the end.
 *
 * @param shape The brush shape.
 * @param brushSize Square size, and the line's thickness, in cells.
 * @param ellipse Ellipse extents in cells.
 * @param from Where the press started.
 * @param to The cell under the pointer now.
 * @param columns Frame grid width in cells.
 * @param rows Frame grid height in cells.
 * @returns Cells as `[column, rowsUp]`, clipped to the grid.
 */
export function footprintBrushCells(shape: CollisionShape, brushSize: number, ellipse: { readonly w: number; readonly h: number }, from: FrameCell, to: FrameCell, columns: number, rows: number): [number, number][] {
  const indices = shape === "pencil" ? cellFootprint(to.x, to.y, 1, columns, rows) : shape === "square" ? cellFootprint(to.x, to.y, brushSize, columns, rows) : shape === "ellipse" ? ellipseCells(to.x, to.y, ellipse.w, ellipse.h, columns, rows) : shape === "rect" ? rectCells(from.x, from.y, to.x, to.y, columns, rows) : lineCells(from.x, from.y, to.x, to.y, brushSize, columns, rows);
  return indices.map((index) => [index % columns, Math.floor(index / columns)]);
}
